import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { DEVELOPMENT_COPY, EMPTY_STATES } from '@saesak/shared';
import { AppBar, ChipRow, Screen } from '@/components/layout';
import { SeedlingTree, StageBadge, StageScale } from '@/components/development';
import { Card, Chip, EmptyState, ErrorNote, SectionTitle, Spinner } from '@/components/ui';
import { apiData } from '@/lib/api';
import { formatDate, withParticle } from '@/lib/format';
import { useChildren } from '@/store/auth';
import type { DevelopmentTree, PeriodRef } from '@/types';

interface TimelinePoint {
  periodCode: string;
  periodLabel: string;
  publishedAt: string | null;
  domains: { domainCode: string; level: number }[];
}

interface PublishedRecord {
  id: string;
  summary: string | null;
  publishedAt: string | null;
  period: PeriodRef;
}

/**
 * 돋보기 — 발달 화면 (§6.8)
 *
 * 자기 비교 원칙(§6.1-3): 또래 대비 비교를 절대 제공하지 않고 아이 자신의 시간 축만 보여 준다.
 * 색은 그린 계열만 쓴다(§7.4).
 */
export function ScopePage() {
  const children = useChildren();
  const [params, setParams] = useSearchParams();
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);

  const childId = params.get('childId') ?? children[0]?.id ?? '';
  const child = children.find((c) => c.id === childId);

  const treeQuery = useQuery({
    queryKey: ['development', 'tree', childId],
    queryFn: () => apiData<DevelopmentTree>(`/development/tree?childId=${childId}`),
    enabled: Boolean(childId),
    placeholderData: keepPreviousData, // 아이 전환 시 화면 유지
  });

  const timelineQuery = useQuery({
    queryKey: ['development', 'timeline', childId],
    queryFn: () => apiData<TimelinePoint[]>(`/development/timeline?childId=${childId}`),
    enabled: Boolean(childId),
    placeholderData: keepPreviousData,
  });

  const recordsQuery = useQuery({
    queryKey: ['development', 'records', childId],
    queryFn: () => apiData<PublishedRecord[]>(`/development/records?childId=${childId}`),
    enabled: Boolean(childId),
    placeholderData: keepPreviousData,
  });

  if (children.length === 0) {
    return (
      <Screen>
        <AppBar title={DEVELOPMENT_COPY.tabName} back />
        <EmptyState title={EMPTY_STATES.children.title} body={EMPTY_STATES.children.body} />
      </Screen>
    );
  }

  const tree = treeQuery.data;
  const hasRecord = Boolean(tree?.period);

  return (
    <Screen>
      <AppBar
        title={DEVELOPMENT_COPY.tabName}
        subtitle={child ? `${child.name} · ${tree?.period?.label ?? '기록 준비 중'}` : undefined}
        back
      />

      <div className="p-4">
        {children.length > 1 && (
          <ChipRow>
            {children.map((item) => (
              <Chip
                key={item.id}
                tone={item.id === childId ? 'primary' : 'neutral'}
                selected={item.id === childId}
                onClick={() => setParams({ childId: item.id })}
              >
                {item.name}
              </Chip>
            ))}
          </ChipRow>
        )}

        {/* §6.1-3 자기 비교 원칙 — 화면에 상시 노출 */}
        <p className="mt-3 flex items-start gap-2 rounded-sm bg-primary-soft px-3 py-2.5 text-sm text-primary-strong">
          <Info size={16} className="mt-0.5 shrink-0" />
          <span>
            <b>{DEVELOPMENT_COPY.everyChildOwnPace}</b>
            <br />
            {DEVELOPMENT_COPY.noComparisonNotice}
          </span>
        </p>

        {treeQuery.isPending && <Spinner label="성장 기록을 불러오는 중이에요" />}
        {treeQuery.isError && (
          <ErrorNote message="기록을 불러오지 못했어요." onRetry={() => void treeQuery.refetch()} />
        )}

        {tree && !hasRecord && (
          <EmptyState title={DEVELOPMENT_COPY.emptyTitle} body={DEVELOPMENT_COPY.emptyBody} />
        )}

        {tree && hasRecord && (
          <>
            <SectionTitle>{DEVELOPMENT_COPY.treeTitle}</SectionTitle>
            <SeedlingTree
              branches={tree.branches}
              selected={selectedDomain}
              onSelect={(code) => setSelectedDomain((prev) => (prev === code ? null : code))}
            />

            {tree.summary && (
              <Card className="mt-4">
                <p className="text-sm font-semibold text-primary-strong">
                  {tree.period?.label} 선생님 이야기
                </p>
                <p className="mt-1.5 text-base leading-relaxed">{tree.summary}</p>
                {tree.updatedAt && (
                  <p className="mt-2 text-sm text-ink-sub">{formatDate(tree.updatedAt)} 공개</p>
                )}
              </Card>
            )}

            <SectionTitle>단계 살펴보기</SectionTitle>
            <Card>
              <StageScale
                current={
                  selectedDomain
                    ? (tree.branches.find((b) => b.domainCode === selectedDomain)?.level ?? undefined)
                    : undefined
                }
              />
              <p className="mt-3 text-sm text-ink-sub">{DEVELOPMENT_COPY.evidenceHint}</p>
            </Card>

            {timelineQuery.data && timelineQuery.data.length > 0 && (
              <>
                <SectionTitle>
                  {child ? `${withParticle(child.name, '이의', '의')} 시간 축` : '시간 축'}
                </SectionTitle>
                <Card>
                  <Timeline points={timelineQuery.data} highlight={selectedDomain} />
                </Card>
              </>
            )}

            {recordsQuery.data && recordsQuery.data.length > 0 && (
              <>
                <SectionTitle>구간별 기록</SectionTitle>
                <ul className="flex flex-col gap-3">
                  {recordsQuery.data.map((record) => (
                    <Card key={record.id} as="li">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold">{record.period.label}</p>
                        <span className="text-sm text-ink-sub">
                          {formatDate(record.publishedAt)}
                        </span>
                      </div>
                      {record.summary && (
                        <p className="mt-1.5 text-base leading-relaxed text-ink">{record.summary}</p>
                      )}
                    </Card>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </div>
    </Screen>
  );
}

/**
 * 시간축 그래프 — 아이 자신의 변화만 보여 준다.
 * 세로축은 1–7단계, 가로축은 구간. 또래 평균선 같은 비교 기준선은 넣지 않는다.
 */
function Timeline({ points, highlight }: { points: TimelinePoint[]; highlight: string | null }) {
  const domains = [...new Set(points.flatMap((p) => p.domains.map((d) => d.domainCode)))];
  const shown = highlight ? domains.filter((d) => d === highlight) : domains;

  return (
    <div className="flex flex-col gap-3">
      {shown.map((domainCode) => (
        <div key={domainCode}>
          <p className="mb-1 text-sm font-medium text-ink-sub">{domainCode}</p>
          <ol className="flex items-end gap-2">
            {points.map((point) => {
              const level = point.domains.find((d) => d.domainCode === domainCode)?.level;
              return (
                <li key={point.periodCode} className="flex flex-1 flex-col items-center gap-1">
                  {level ? (
                    <StageBadge level={level} showName={false} size="sm" />
                  ) : (
                    <span className="text-sm text-ink-sub">–</span>
                  )}
                  <span className="text-[11px] text-ink-sub">{point.periodLabel}</span>
                </li>
              );
            })}
          </ol>
        </div>
      ))}
    </div>
  );
}
